
import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { useLocation } from 'react-router-dom';
import {
    FiSearch,
    FiAlertCircle,
    FiBox,
    FiChevronLeft,
    FiChevronRight,
    FiX,
    FiLoader,
    FiCheckCircle,
} from 'react-icons/fi';

export default function Withdrawal() {
    const location = useLocation();

    // create → stock withdrawal
    // list → withdrawal list
    const activeTab = location.pathname.includes('create') ? 'create' : 'list';

    // ============================
    // Table State
    // ============================
    const [data, setData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState('');

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // ============================
    // Popup State
    // ============================
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [withdrawQty, setWithdrawQty] = useState('');
    const [remarks, setRemarks] = useState('');
    const [popupError, setPopupError] = useState('');
    const [popupSuccess, setPopupSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ============================
    // Filter State
    // ============================
    const [filters, setFilters] = useState({
        state_id: '',
        purpose_id: '',
        warehouse_id: '',
        scheme_id: '',
        commodity_id: '',
        whr_id: '',
    });

    const [states, setStates] = useState([]);
    const [purposes, setPurposes] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [schemes, setSchemes] = useState([]);
    const [commodities, setCommodities] = useState([]);

    const [dropdownOpen, setDropdownOpen] = useState({
        state: false,
        purpose: false,
        warehouse: false,
        scheme: false,
        commodity: false,
    });

    const [search, setSearch] = useState({
        state: '',
        purpose: '',
        warehouse: '',
        scheme: '',
        commodity: '',
    });

    const token = localStorage.getItem('token');
    const initRef = useRef(false);

    // ============================
    // FETCH FILTERS
    // ============================
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [statesRes, purposesRes, warehousesRes, schemesRes, commoditiesRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BASE_URL}states`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}purposes?paginate=false`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}warehouse?paginate=false`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}schemes`),
                    axios.get(`${process.env.REACT_APP_BASE_URL}commodity`),
                ]);
                setStates(statesRes.data || []);
                setPurposes(purposesRes.data || []);
                setWarehouses(warehousesRes.data || []);
                setSchemes(schemesRes.data || []);
                setCommodities(commoditiesRes.data || []);
            } catch (err) {
                console.error("Failed to fetch filters", err);
            }
        };
        fetchFilters();
    }, []);



    // ============================
    // CLICK OUTSIDE TO CLOSE DROPDOWNS
    // ============================
    // ============================
    // CLICK OUTSIDE TO CLOSE DROPDOWNS
    // ============================
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container')) {
                setDropdownOpen({
                    state: false,
                    purpose: false,
                    warehouse: false,
                    scheme: false,
                    commodity: false,
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // ============================
    // FETCH LIST DATA (ONLY page & limit)
    // ============================
    const fetchData = async () => {
        if (activeTab !== 'list') return;

        setTableLoading(true);
        setError('');

        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', limit);
            if (filters.state_id) params.append('state_id', filters.state_id);
            if (filters.warehouse_id) params.append('warehouse_id', filters.warehouse_id);
            if (filters.purpose_id) params.append('purpose_id', filters.purpose_id);
            if (filters.scheme_id) params.append('scheme_id', filters.scheme_id);
            if (filters.commodity_id) params.append('commodity_id', filters.commodity_id);
            if (filters.whr_id) params.append('whr_id', filters.whr_id);

            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}stock-withdrawal?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setData(response.data.data || []);
            setTotalPages(Number(response.data.totalPages) || 1);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch withdrawal list');
            setData([]);
        } finally {
            setTableLoading(false);
        }
    };

    // ============================
    // INITIAL + PAGINATION + FILTER FETCH
    // ============================
    useEffect(() => {
        if (activeTab === 'list') fetchData();
        // eslint-disable-next-line
    }, [page, activeTab, filters]);

    // ============================
    // WITHDRAWAL SAVE (POST)
    // ============================
    const handleSaveWithdrawal = async () => {
        if (!withdrawQty || Number(withdrawQty) <= 0) {
            setPopupError('Enter valid quantity');
            return;
        }

        setIsSubmitting(true);
        setPopupError('');

        try {
            const payload = {
                withdrawn_qty: Number(withdrawQty),
                remarks: remarks || 'Withdrawal Remark',
                block_id: selectedItem.id,
            };

            const res = await axios.post(
                `${process.env.REACT_APP_BASE_URL}stock-withdrawal`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setPopupSuccess(res.data.message || 'Stock withdrawn');

            setTimeout(() => {
                setIsPopupOpen(false);
                fetchData();
            }, 1000);
        } catch (err) {
            setPopupError(err.response?.data?.message || 'Withdrawal failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ============================
    // HANDLERS
    // ============================
    const handleSelect = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setDropdownOpen((prev) => ({ ...prev, [name]: false }));
        setPage(1);
    };

    const clearFilter = (name) => {
        setFilters((prev) => ({ ...prev, [name]: '' }));
        setSearch((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSearch = () => {
        // No longer needed as button is removed
    };

    const renderDropdown = (label, name, items, labelKey) => {
        const isOpen = dropdownOpen[name];
        const selectedValue = filters[name];
        const searchTerm = search[name];

        return (
            <div className="relative w-[250px] max-w-full dropdown-container">
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen((prev) => ({
                            state: false,
                            purpose: false,
                            warehouse: false,
                            scheme: false,
                            commodity: false,
                            [name]: !prev[name],
                        }));
                    }}
                    className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center h-[50px]"
                >
                    <span className="truncate mr-2">
                        {selectedValue
                            ? items.find((i) => i.id === selectedValue)?.[labelKey] ||
                            `${label} Selected`
                            : `Select ${label}`}
                    </span>
                    <div className="flex items-center gap-2">
                        {selectedValue && (
                            <FiX
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearFilter(name);
                                }}
                                className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                title="Clear"
                            />
                        )}
                        <span className="text-gray-500">{isOpen ? '▲' : '▼'}</span>
                    </div>
                </div>

                {isOpen && (
                    <div
                        className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <input
                            type="text"
                            placeholder={`Search ${label}...`}
                            value={searchTerm}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                                setSearch((prev) => ({ ...prev, [name]: e.target.value }))
                            }
                            className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                        />
                        {items
                            .filter((i) => {
                                const itemLabel = i && i[labelKey] ? String(i[labelKey]) : '';
                                return itemLabel.toLowerCase().includes((searchTerm || '').toLowerCase());
                            })
                            .map((i) => (
                                <div
                                    key={i.id}
                                    onClick={() => handleSelect(name, i.id)}
                                    className={`p-2 cursor-pointer hover:bg-blue-100 ${filters[name] === i.id ? 'bg-blue-200 font-semibold' : ''}`}
                                >
                                    {i[labelKey]}
                                </div>
                            ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10 text-gray-900">
            <div className="flex items-center gap-3 mb-6">
                <FiBox className="text-3xl text-blue-700" />
                <h2 className="text-3xl font-extrabold text-blue-800 tracking-tight">
                    {activeTab === 'create' ? 'Stock Withdrawal' : 'Withdrawal List'}
                </h2>
            </div>

            {/* FILTERS (Only for List Tab) */}
            {activeTab === 'list' && (
                <div className="flex flex-wrap gap-4 mb-6 items-center justify-start">
                    {/* Text Search for WHR ID */}
                    <div className="relative w-[250px] max-w-full">
                        <input
                            type="text"
                            placeholder="Search by WHR ID..."
                            value={filters.whr_id}
                            onChange={(e) => handleSelect('whr_id', e.target.value)}
                            className="w-full h-[50px] px-4 py-2 border rounded-xl bg-white shadow-sm text-base focus:outline-none focus:ring-2 focus:ring-blue-200"
                        />
                        {filters.whr_id && (
                            <FiX
                                onClick={() => handleSelect('whr_id', '')}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                                title="Clear"
                            />
                        )}
                    </div>

                    {renderDropdown('State', 'state_id', states, 'name')}
                    {renderDropdown('Warehouse', 'warehouse_id', warehouses, 'warehouse_name')}
                    {renderDropdown('Purpose', 'purpose_id', purposes, 'name')}
                    {renderDropdown('Scheme', 'scheme_id', schemes, 'name')}
                    {renderDropdown('Commodity', 'commodity_id', commodities, 'commodityname')}
                </div>
            )}

            {/* ERROR */}
            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
                    <FiAlertCircle /> {error}
                </div>
            )}

            {/* TABLE */}
            <div className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px]">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">State</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Warehouse</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">WHR ID</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Commodity</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Scheme</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Season</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Purpose</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Withdrawn Qty(MT)</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Remarks</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>

                        <tbody>
                            {tableLoading ? (
                                <tr>
                                    <td colSpan="8" className="py-8 text-center">
                                        <FiLoader className="animate-spin inline text-xl" /> Loading...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="py-8 text-center text-gray-500">
                                        No withdrawal records found
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-200">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.state?.name || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            {item.warehouse?.warehouse_name}
                                            <br />
                                            <span className="text-xs text-gray-500">{item.warehouse?.warehouse_code}</span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 font-bold">{item.whr_id}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700">{item.commodity?.commodityname || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                                                {item.scheme?.name || '-'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700">{item.season?.name || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-purple-700 font-medium">{item.purpose?.name || '-'}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-gray-900">
                                            {item.withdrawn_qty}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-green-700 font-medium">{item.remarks || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-blue-700 font-medium">{item.status || '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* PAGINATION */}
            {
                totalPages > 1 && (
                    <div className="flex justify-center gap-4 mt-6">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                            className="px-4 py-2 bg-gray-200 rounded"
                        >
                            <FiChevronLeft /> Prev
                        </button>

                        <span className="font-semibold">
                            Page {page} of {totalPages}
                        </span>

                        <button
                            disabled={page === totalPages}
                            onClick={() => setPage(page + 1)}
                            className="px-4 py-2 bg-gray-200 rounded"
                        >
                            Next <FiChevronRight />
                        </button>
                    </div>
                )
            }
        </div >
    );
}
